import { useCallback, useEffect, useRef, useState } from 'react';

import type { Event, RiveEventPayload } from '@rive-app/react-canvas';
import { Alignment, EventType, Fit, Layout } from '@rive-app/react-canvas';

import { useRiveMachine } from '../../../hooks/rive/useRiveMachine';
import { useFlag } from '../../../services/launchDarkly';
import type { AnimationGuideProgress, RiveFTUEConfig } from '../../../services/types';
import { useStores } from '../../../stores/useStores';
import { INTERACTIVE_GUIDE_EVENTS_ENUM } from '../constants';

import type { RIVE_FTUE_EVENTS } from './constants';
import { defaultConfig, interactiveGuideAnalyticsParamsMap, riveFTUEEventsMap } from './constants';
import styles from './interactiveAnimationFTUE.module.scss';
import type { RiveEventProperties } from './types';

export interface InteractiveAnimationFTUERiveProps {
  onGuideEvent: (
    properties: RiveEventProperties,
    fileUrl: string,
    skipTriggerInput?: { fire: () => void },
    wasInitialSkipTriggered?: boolean,
    skipStepNumber?: number,
    ftueType?: string,
  ) => void;
  terminateTourGuideFlow: (reason: string, animationType: 'rive') => void;
  viewType?: 'desktop' | 'mobile';
  savedStep: AnimationGuideProgress | null;
}

export const InteractiveAnimationFTUERive = ({
  onGuideEvent,
  terminateTourGuideFlow,
  viewType,
  savedStep,
}: InteractiveAnimationFTUERiveProps) => {
  const { userStore } = useStores();

  const [wasInitialSkipTriggered, setWasInitialSkipTriggered] = useState(false);

  const riveConfig = useFlag<RiveFTUEConfig>('rive_ftue_config', defaultConfig);
  const fileConfig = viewType === 'desktop' ? riveConfig?.desktop : riveConfig?.mobile_web || riveConfig?.mobile_m;

  const isRiveRestored = useRef<boolean>(false);

  const { rive, RiveComponent, useStateMachineInput } = useRiveMachine({
    src: fileConfig?.url,
    stateMachineName: fileConfig?.stateMachineName,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: viewType === 'mobile' ? Alignment.TopCenter : Alignment.Center,
    }),
    autoplay: false,
    onLoadError: (error) => {
      terminateTourGuideFlow(error?.data || 'rive_load_error', 'rive');
    },
  });

  const fileConfigMatch =
    savedStep?.firebaseUserId === userStore.profile?.firebaseUid && savedStep?.riveFileName === fileConfig?.url;
  const triggerInput = useStateMachineInput(rive, fileConfig?.stateMachineName || '', savedStep?.eventName, true);
  const skipTriggerInput = useStateMachineInput(rive, fileConfig?.stateMachineName || '', 'skip_trig', true);

  const onRiveEventNameReceived = useCallback(
    (event: RiveEventPayload, riveUrl: string) => {
      if (!event || !('name' in event)) {
        return;
      }
      const { name } = event;
      const correctEventName = riveFTUEEventsMap[name as RIVE_FTUE_EVENTS];
      if (!correctEventName) {
        return;
      }
      if (correctEventName === 'ignorable_event') {
        return;
      }

      const { event_name, step_name } = interactiveGuideAnalyticsParamsMap[correctEventName] || {};

      const properties: RiveEventProperties =
        correctEventName === INTERACTIVE_GUIDE_EVENTS_ENUM.FINISH
          ? {
              isFinish: true,
            }
          : {
              event_name,
              step_name,
              isLastStep: correctEventName === INTERACTIVE_GUIDE_EVENTS_ENUM.ALL_SET,
            };

      onGuideEvent(
        properties,
        riveUrl,
        skipTriggerInput || undefined,
        wasInitialSkipTriggered,
        fileConfig?.skip_tutorial,
        riveConfig?.ftue_type,
      );
    },
    [onGuideEvent, skipTriggerInput, wasInitialSkipTriggered, fileConfig?.skip_tutorial, riveConfig?.ftue_type],
  );

  const onRiveEventWithPropertiesReceived = useCallback(
    (properties: Partial<RiveEventProperties>, riveUrl: string) => {
      onGuideEvent(
        properties as RiveEventProperties,
        riveUrl,
        skipTriggerInput || undefined,
        wasInitialSkipTriggered,
        fileConfig?.skip_tutorial,
        riveConfig?.ftue_type,
      );
    },
    [onGuideEvent, skipTriggerInput, wasInitialSkipTriggered, fileConfig?.skip_tutorial, riveConfig?.ftue_type],
  );

  const onRiveEventReceived = useCallback(
    (event: Event) => {
      if (typeof event.data !== 'object' || !fileConfig?.url) {
        return;
      }
      if ('properties' in event.data && event.data.properties) {
        onRiveEventWithPropertiesReceived(event.data.properties, fileConfig.url);
        return;
      }
      if (!('name' in event.data)) {
        return;
      }
      onRiveEventNameReceived(event.data, fileConfig.url);
    },
    [onRiveEventNameReceived, onRiveEventWithPropertiesReceived, fileConfig?.url],
  );

  useEffect(() => {
    if (
      skipTriggerInput &&
      fileConfig?.skip_tutorial &&
      fileConfig?.skip_tutorial <= (savedStep?.stageNum || 0) &&
      fileConfigMatch &&
      !wasInitialSkipTriggered
    ) {
      skipTriggerInput.fire();
      setWasInitialSkipTriggered(true);
    }
  }, [skipTriggerInput, fileConfig?.skip_tutorial, fileConfigMatch, wasInitialSkipTriggered, savedStep?.stageNum]);

  useEffect(() => {
    if (rive) {
      rive.off(EventType.RiveEvent, onRiveEventReceived);
      rive.on(EventType.RiveEvent, onRiveEventReceived);
      rive.play();
    }
    return () => {
      if (rive) {
        rive.off(EventType.RiveEvent, onRiveEventReceived);
      }
    };
  }, [rive, onRiveEventReceived]);

  useEffect(() => {
    if (rive && triggerInput && fileConfigMatch && !isRiveRestored.current) {
      triggerInput.fire();
      isRiveRestored.current = true;
    }
  }, [triggerInput, fileConfigMatch, rive]);

  return <RiveComponent className={styles.animationContainer} />;
};
